/**
 * Canonical AppResponse envelope helpers for admin-portal.
 *
 * WHY THIS MODULE EXISTS
 * ----------------------
 * core-api reports BUSINESS failures inside an HTTP 200 body:
 *
 *   { errorCode: '0',    message: null,          details: null,  data: {...} }  // success
 *   { errorCode: '1027', message: 'INVALID ...', details: '...', data: null }   // failure
 *
 * Because the failure rides inside a 2xx response, `.unwrap()` on an RTK Query
 * trigger does NOT reject on it — a page that unwraps and destructures only
 * "fails" by accident, when a `data: null` body happens to throw. Until now the
 * only correct check in this repo was an unexported module-local copy inside
 * src/modules/auth/forget-password/page.tsx. This module is the single exported
 * copy; new call sites import it instead of re-inventing (and re-breaking) it.
 *
 * AppResponse's default constructor sets errorCode "0" on success (verified in
 * core-api AppResponse.java), so `errorCode === '0'` is a safe success
 * predicate. Do NOT branch on errorCode '1030': that is the generic
 * CUSTOM_ERROR code shared by EVERY custom error in that backend (an invalid or
 * expired reset token, a mail-delivery failure, ...). Match a specific business
 * outcome on `message`, the way ForgetPassword matches 'EMAIL FAILED'.
 *
 * THE TWO ERROR DIALECTS IN THIS REPO — RECONCILED, NOT MERGED
 * ------------------------------------------------------------
 * admin-portal speaks two different error dialects, and they disagree. Both
 * stay; this is the division of labour, and it is the rule for new code:
 *
 *   1. THIS MODULE — the `errorCode` envelope — is CANONICAL for BODY-LEVEL
 *      BUSINESS OUTCOMES. Whether an operation succeeded is decided here and
 *      nowhere else: SUCCESS iff the resolved body carries errorCode '0'.
 *
 *   2. src/modules/companies/utils/get-api-error-message.ts keys on the body's
 *      `httpStatus` string ('OK' / 'NOT_FOUND' / ...). core-api controllers
 *      return a BARE AppResponse, so the transport status is 200 and that body
 *      `httpStatus` field is DECORATION — it is populated inconsistently and
 *      must not be used to decide success or failure. `getApiErrorMessage`
 *      keeps a legitimate, narrower job: extracting human-readable copy from a
 *      TRANSPORT-SHAPED error (a thrown / `{ error }` FetchBaseQueryError —
 *      FETCH_ERROR, PARSING_ERROR, a real 4xx/5xx). Use it for the message on a
 *      TRANSPORT_FAILURE; never to classify a resolved body.
 *
 *   THE CONCRETE DISAGREEMENT: a 1030 reply — HTTP 200,
 *   `{ errorCode: '1030', httpStatus: 'OK', message, details, data: '' }` — is
 *   a BODY_FAILURE here, and this module's verdict is the one that counts.
 *   `getAppResponseError` would read that same body's `httpStatus: 'OK'` and,
 *   only because `data` is falsy, fall through to "The server returned an empty
 *   response." — right answer for the wrong reason, and plain wrong on any
 *   failure body that happens to carry a non-empty `data`. Classify with
 *   `classifyMutationResult`; if you also want copy, take it from
 *   `extractResponsePayload(...).message`.
 *
 * NO GLOBAL ERROR REPORTING EXISTS IN THIS REPO — THE SILENT BAND IS TOTAL
 * ------------------------------------------------------------------------
 * Unlike company-portal, `baseQueryWithReAuth` in src/services/api-slice.ts
 * inspects NOTHING in response bodies. It handles only the HTTP 401
 * refresh/logout dance; there is no global 1030 toast, no 400/500 toast, and no
 * toast system at all outside four companies pages' local Chakra useToast
 * calls. So: EVERY unhandled failure in admin-portal is invisible to the user.
 *
 * The consequence for a call site: reporting is entirely yours. On anything
 * that is not SUCCESS — do not show a success message, do not navigate away, do
 * not close the drawer/modal, do not persist anything (a `data: ''` body
 * destructures to `undefined`, and persisting that writes the literal string
 * 'undefined'), leave the user's input intact, and make the failure VISIBLE
 * (an inline `role="alert"` region, as ForgetPassword does).
 */

/** The AppResponse envelope fields a call site is allowed to reason about. */
export type AppResponsePayload = {
  errorCode?: string;
  message?: string | null;
  details?: string | null;
};

/** The three ways an awaited, NON-unwrapped mutation trigger can land. */
export const MutationResult = {
  /** 2xx with a body-level success (`errorCode === '0'`). */
  SUCCESS: 'SUCCESS',
  /** The request was answered, but the envelope says the operation failed. */
  BODY_FAILURE: 'BODY_FAILURE',
  /** No usable answer at all: network error, timeout, or a non-2xx status. */
  TRANSPORT_FAILURE: 'TRANSPORT_FAILURE',
} as const;

export type MutationResultKind =
  (typeof MutationResult)[keyof typeof MutationResult];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Normalise ANY awaited mutation result to the AppResponse envelope it carries.
 *
 * Handles the shapes core-api and RTK Query actually produce:
 *   { data: { errorCode, message, details, data } }  → that body
 *   { error: { status, data: {...} } }               → the error body
 *   { data: '' } / { data: null }                    → {} (failure replies)
 *   anything else                                    → {}
 *
 * It ALWAYS returns an object, so `payload.message` is safe to read without a
 * guard. It never throws. NOTE this is a deliberate fix to the old page-local
 * copy's `r?.data ?? r?.error?.data ?? {}`, which handed the raw `''` back for a
 * `data: ''` reply because `??` does not catch the empty string.
 */
export const extractResponsePayload = (
  response: unknown
): AppResponsePayload => {
  if (!isRecord(response)) return {};

  // A failure reply can carry `data: ''` or `data: null`; neither is an
  // envelope, so fall through rather than handing a string back to the caller.
  if (isRecord(response.data)) return response.data as AppResponsePayload;

  if (isRecord(response.error) && isRecord(response.error.data)) {
    return response.error.data as AppResponsePayload;
  }

  return {};
};

/**
 * True only for a body-level success. `data: ''`, `data: null`, a non-'0'
 * errorCode and the `{ error }` transport shape are all false.
 */
export const isSuccess = (response: unknown): boolean => {
  if (!isRecord(response)) return false;
  if (!isRecord(response.data)) return false;
  return (response.data as AppResponsePayload).errorCode === '0';
};

/**
 * Classify the raw awaited result of a NON-unwrapped RTK Query mutation
 * trigger. Fails closed: anything that is not provably a body-level success is
 * a failure, so a new backend error code can never read as success.
 */
export const classifyMutationResult = (
  response: unknown
): MutationResultKind => {
  // Not an object at all (undefined from an aborted call, say) — treat as
  // "no usable answer", the same bucket as a network error.
  if (!isRecord(response)) return MutationResult.TRANSPORT_FAILURE;

  // RTK Query resolves a non-unwrapped trigger to `{ error }` for BOTH a raw
  // network failure and a non-2xx status. First-class shape, not an edge case.
  if ('error' in response && response.error !== undefined) {
    return MutationResult.TRANSPORT_FAILURE;
  }

  return isSuccess(response)
    ? MutationResult.SUCCESS
    : MutationResult.BODY_FAILURE;
};
